import type { FC } from 'react';
import React from 'react';

import { Box, CircularProgress, useTheme } from '@mui/material';
import Lottie from 'react-lottie';

import { MAX_RATE_COMPLETE } from '@pages/common/Onboarding/const/max-rate-complete';
import { getOnboardingCompleteSelector } from '@pages/common/Onboarding/store/onboarding-selectors';
import { setOnboardingCompleteAnimation } from '@pages/common/Onboarding/store/onboarding-slice';
import { OnboardingSetupRocket } from '@setup/svg-icons/svg-icons';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';

import setupAnimation from '../../const/default-rocket-animation.json';
import setupCompleteRocketAnimation from '../../const/progress-circle_checkmark.json';
import setupCompletePulseAnimation from '../../const/progress-circle_pulse.json';

type OnboardingRocketPropsType = {
  isStopAnimation?: boolean;
};

const defaultCompleteOptions = {
  loop: true,
  autoplay: true,
  animationData: setupCompletePulseAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const defaultCompleteRocketOptions = {
  loop: false,
  autoplay: true,
  animationData: setupCompleteRocketAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};
const defaultRocketOptions = {
  loop: true,
  autoplay: true,
  animationData: setupAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};
export const OnboardingRocket: FC<OnboardingRocketPropsType> = ({ isStopAnimation = false }) => {
  const progress = useAppSelector(getOnboardingCompleteSelector);

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isCompleteAnimation = useAppSelector((state) => state.onboardingSetup.isCompleteProcessAnimation);
  const isCompeteOnboarding = progress >= MAX_RATE_COMPLETE;

  const options =
    isCompeteOnboarding && !isCompleteAnimation
      ? defaultCompleteRocketOptions
      : isCompeteOnboarding && isCompleteAnimation
        ? defaultCompleteOptions
        : defaultRocketOptions;

  return (
    <Box
      sx={{ borderRadius: '50%', position: 'relative', boxShadow: '0px 4px 16px 0px rgba(38, 44, 74, 0.16)' }}
    >
      <CircularProgress
        variant="determinate"
        sx={{
          color: theme.palette.case.neutral.n200,
          visibility: isCompeteOnboarding ? 'hidden' : 'visible',
        }}
        size={40}
        thickness={4}
        value={100}
      />
      <CircularProgress
        thickness={4}
        size={40}
        variant="determinate"
        value={progress}
        sx={{
          visibility: isCompeteOnboarding ? 'hidden' : 'visible',
          color: theme.palette.case.aquamarine.a600,
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!isStopAnimation && (
          <Lottie
            isClickToPauseDisabled={true}
            options={options}
            height={48}
            width={48}
            eventListeners={[
              {
                eventName: 'complete',
                callback: () => {
                  dispatch(setOnboardingCompleteAnimation({ isComplete: true }));
                },
              },
            ]}
          />
        )}

        {isStopAnimation && <OnboardingSetupRocket />}
      </Box>
    </Box>
  );
};
